import { useAui } from '@assistant-ui/react';

import type { ProduceDatasetFn } from '#plugin-chat/lib/tool/create-tool.types.ts';
import {
  extractOutputKeys,
  extractToolCallParts,
  findToolCallByOutputKey,
} from '#plugin-chat/lib/tool/output-extraction.ts';
import type { ToolCallPart } from '#plugin-chat/lib/tool/output-types.ts';

/**
 * Returns imperative accessor functions that read fresh messages at call time.
 * Use for ToolParameterContext building and schema resolution.
 *
 * The returned functions are stable (don't change between renders) because
 * useAui returns a stable API object.
 */
export function useToolOutputAccessors() {
  // FIXME: - update functions when messages are updated - after streaming AND before sendMessages
  const aui = useAui();

  // Imperative message access - reads fresh at call time
  const getMessages = (): readonly unknown[] => aui.thread().getState().messages.toReversed();

  return {
    /**
     * Get all available output keys from current messages.
     * Reads fresh messages at call time.
     */
    getOutputKeys: (): string[] => {
      const parts = extractToolCallParts(getMessages());
      return extractOutputKeys(parts);
    },

    /**
     * Get full tool call part by output key.
     * Reads fresh messages at call time.
     */
    getToolCall: (key: string): ToolCallPart | undefined => {
      const parts = extractToolCallParts(getMessages());
      // console.log(findToolCallByOutputKey(parts, key));

      //   const discriminators = toolDef.parameters ? extractDiscriminators(toolDef.parameters) : new Map<string, string>();
      // const args =
      //   discriminators.size > 0 ? (convertTaggedToDiscriminated(data, discriminators) as ArgsType) : (data as ArgsType);
      // console.log(queryKey);
      return findToolCallByOutputKey(parts, key);
    },

    /**
     * Look up a producer tool's `produceDataset` function by tool name,
     * read back from the assistant-ui model context where `createTool`
     * attaches it on registration. Returns `undefined` for non-producer
     * tools or tools that haven't registered yet (gated / ungated before
     * mount). The dataset resolver uses this to materialise datasets
     * lazily — independent of whether the tool-call UI has rendered.
     */
    getProducer: (toolName: string): ProduceDatasetFn | undefined => {
      const entry = (
        aui.modelContext().getModelContext().tools as Record<string, { produceDataset?: ProduceDatasetFn }>
      )[toolName];
      return entry?.produceDataset;
    },
  };
}
